import React, { memo, useEffect, useMemo, useState } from 'react';
import styles from './Workers.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { getActiveSalonId, getActiveSalon } from '../../../Auth/store/selectors';
import { isDemoMode } from 'demo';
import { getWorkers } from '../../store/selectors';
import { salonManagementActions } from '../../store';
import TitleSectionBack from '../../../Common/TitleSectionBack';
import useWindowSize from '../../../../utils/useWindowSize';
import clx from 'classnames';
import { List, arrayMove } from 'react-movable';
import { useTranslation } from 'react-i18next';
import { Armchair, Ban, CheckCircle2, EyeOff, Plus } from 'lucide-react';

import ConfirmDeleteModal from '../../../Common/ConfirmDeleteModal';
import { WorkerItem } from './containers/WorkerItem';
import { modalActions } from '../../../Modals/store';

const FILTERS = [
  { id: 1, key: 'active', icon: CheckCircle2, tone: 'active' },
  { id: 2, key: 'disabled', icon: Ban, tone: 'disabled' },
  { id: 3, key: 'onlyVisibleOnManagement', icon: EyeOff, tone: 'hidden' },
];

const Workers = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [activeFilterId, setActiveFilterId] = useState(1);
  const activeSalonId = useSelector(getActiveSalonId());
  const activeSalon = useSelector(getActiveSalon());
  const demoMode = isDemoMode(activeSalon);
  const workers = useSelector(getWorkers(activeFilterId, true));
  const allWorkers = useSelector(getWorkers(0, false));
  const { isMobile } = useWindowSize();

  const stats = useMemo(
    () => ({
      1: allWorkers.filter((worker) => worker.visible && !worker.disabilitato).length,
      2: allWorkers.filter((worker) => worker.disabilitato).length,
      3: allWorkers.filter((worker) => !worker.visible && !worker.disabilitato).length,
    }),
    [allWorkers],
  );

  useEffect(() => {
    activeSalonId && dispatch(salonManagementActions.GET_WORKERS.REQUEST({}, null, { skipLoader: true }));
  }, [activeSalonId]);

  const openCreateWorkerModal = () => {
    dispatch(
      modalActions.OPEN_MODAL.SUCCESS({
        type: 'createWorkerModal',
      }),
    );
  };

  const addWorkerButton = (
    <button type="button" className={styles.add_worker_btn} onClick={openCreateWorkerModal}>
      <Plus size={16} strokeWidth={2.4} aria-hidden="true" />
      <span>{t('workers.addWorker')}</span>
    </button>
  );

  return (
    <div
      className={clx(styles.workers_container, {
        [styles.workers_container_mobile]: isMobile,
      })}
    >
      <div className={styles.header}>
        <TitleSectionBack
          title={t('workers.header.title')}
          description={t('workers.header.description')}
        />
        {!isMobile && !demoMode && addWorkerButton}
      </div>

      {isMobile && !demoMode ? <div className={styles.mobile_toolbar}>{addWorkerButton}</div> : null}

      <div className={styles.list_card}>
        <div className={styles.filters_row}>
          {FILTERS.map((filter) => {
            const Icon = filter.icon;
            const isActive = activeFilterId === filter.id;
            return (
              <button
                key={filter.id}
                type="button"
                className={clx(styles.filter_chip, styles[`filter_chip_${filter.tone}`], {
                  [styles.filter_chip_selected]: isActive,
                })}
                aria-pressed={isActive}
                onClick={() => setActiveFilterId(filter.id)}
              >
                <span className={styles.filter_chip_icon}>
                  <Icon size={16} strokeWidth={2.2} />
                </span>
                <span className={styles.filter_chip_copy}>
                  <span className={styles.filter_chip_name}>
                    {t(`workers.filters.${filter.key}`)}
                  </span>
                  <span className={styles.filter_chip_value}>{stats[filter.id] ?? 0}</span>
                </span>
              </button>
            );
          })}
        </div>

        {workers?.length ? (
          <WorkersList workers={workers} />
        ) : (
          <div className={styles.empty_state}>
            <Armchair size={22} strokeWidth={2.2} aria-hidden="true" />
            <strong>{t('workers.empty')}</strong>
            <span>{t('workers.emptyHint')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const WorkersList = memo(
  ({ workers = [] }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [items, setItems] = useState(workers);
    const [workerToDeleteId, setWorkerToDeleteId] = useState(false);
    const [workerToEnableId, setWorkerToEnableId] = useState(false);

    useEffect(() => {
      setItems(workers);
    }, [workers]);

    useEffect(() => {
      if (items.length && workers.length) {
        const changedWorkersPosition = workers.reduce((acc, worker, index) => {
          if (+worker.id === +items[index].id) return acc;

          const newPosition = items.findIndex(
            (workerWithNewPosition) => +workerWithNewPosition.id === +worker.id,
          );
          const workerNewPosition = {
            workerId: +worker.id,
            position: +newPosition + 1,
          };
          return [...acc, workerNewPosition];
        }, []);

        if (changedWorkersPosition.length) {
          dispatch(salonManagementActions.SET_WORKERS_POSITION.REQUEST(changedWorkersPosition));
        }
      }
    }, [items]);

    return (
      <>
        <ConfirmDeleteModal
          isModalOpen={workerToDeleteId}
          setIsModalOpen={setWorkerToDeleteId}
          title={t('workers.confirmDelete.title')}
          buttons={[t('common.actions.cancel'), t('common.actions.disable')]}
          action={() =>
            dispatch(
              salonManagementActions.DELETE_WORKER.REQUEST(
                {
                  idPoltrona: workerToDeleteId,
                  disabilitato: 1,
                },
                () => setWorkerToDeleteId(false),
              ),
            )
          }
        />
        <ConfirmDeleteModal
          isModalOpen={workerToEnableId}
          setIsModalOpen={setWorkerToEnableId}
          title={t('workers.confirmEnable.title')}
          buttons={[t('common.actions.cancel'), t('common.actions.enable')]}
          buttonVariants={['default', 'primary']}
          Icon={null}
          action={() =>
            dispatch(
              salonManagementActions.ENABLE_WORKER.REQUEST(
                {
                  idPoltrona: workerToEnableId,
                  disabilitato: 0,
                },
                () => setWorkerToEnableId(false),
              ),
            )
          }
        />
        <List
          values={items}
          onChange={({ oldIndex, newIndex }) => {
            const arrayMoved = arrayMove(workers, oldIndex, newIndex);
            setItems(arrayMoved);
          }}
          renderList={({ children, props, isDragged }) => (
            <ul
              {...props}
              className={styles.workers_list}
              style={{
                cursor: isDragged ? 'grabbing' : undefined,
              }}
            >
              {children}
            </ul>
          )}
          renderItem={({ value: worker, props, isDragged }) => (
            <li
              {...props}
              key={worker.id}
              className={styles.worker_li}
              style={{
                ...props.style,
                listStyleType: 'none',
                cursor: isDragged ? 'grabbing' : 'inherit',
                position: isDragged ? 'fixed' : 'relative',
              }}
            >
              <WorkerItem
                isDragged={isDragged}
                worker={worker}
                setWorkerToDeleteId={setWorkerToDeleteId}
                setWorkerToEnableId={setWorkerToEnableId}
              />
            </li>
          )}
        />
      </>
    );
  },
  (prevProps, nextProps) => {
    return JSON.stringify(prevProps) === JSON.stringify(nextProps);
  },
);

export default Workers;
